import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { getClientReportsList, getClientReportById } from '../utilities/api';

/**
 * Client Report Checking — client → branch → user tree for every Active client.
 *
 *   GET {base}/api/client_reports/list              → all Active clients (left list, loaded on open)
 *   GET {base}/api/client_reports/list/{client_id}  → one client (right pane, on selection)
 *
 * Both are Active-only and take no request body.
 */
/** Clients kept out of this view entirely — GSMASTER (29) is the internal master account, not a customer. */
const HIDDEN_CLIENT_IDS = ['29'];
const isHiddenClient = (client) => HIDDEN_CLIENT_IDS.includes(String(client?.client_id));

const clientsOf = (data) => (Array.isArray(data?.clients) ? data.clients : []);
const branchesOf = (client) => (Array.isArray(client?.branches) ? client.branches : []);
const usersOf = (node) => (Array.isArray(node?.users) ? node.users : []);
const clientUsersOf = (client) => (Array.isArray(client?.client_users) ? client.client_users : []);

const clientUserCount = (client) => branchesOf(client).reduce((n, b) => n + usersOf(b).length, 0) + clientUsersOf(client).length;

/** Rolls the whole list up into the counts shown in the hero. */
const treeTotals = (data) => {
    const clients = clientsOf(data);
    return {
        clients: clients.length,
        branches: clients.reduce((n, c) => n + branchesOf(c).length, 0),
        users: clients.reduce((n, c) => n + clientUserCount(c), 0)
    };
};

const val = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const isHttpUrl = (u) => /^https?:\/\/\S+$/i.test(String(u || '').trim());

/** report_url may hold several addresses — comma, semicolon, pipe or whitespace separated. */
const splitReportUrls = (raw) =>
    String(raw || '')
        .split(/[,;|\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);

/**
 * Reachability check for a client's report_url.
 *
 * The report hosts are cross-origin and don't send CORS headers, so the response is
 * opaque: we can tell that the server answered, but not what it answered with. A
 * resolved request therefore means "host is up and serving", NOT "the report renders" —
 * a 404 or 500 also resolves. A rejection means DNS failure, refused connection, bad
 * SSL, timeout, or mixed-content blocking. For a true status code the backend would
 * have to make the request and report it back.
 */
const REACH_TIMEOUT_MS = 8000;

const probeUrl = async (url) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REACH_TIMEOUT_MS);
    const t0 = Date.now();
    try {
        await fetch(url, { mode: 'no-cors', cache: 'no-store', redirect: 'follow', signal: controller.signal });
        return { state: 'up', ms: Date.now() - t0 };
    } catch (e) {
        return { state: 'down', ms: Date.now() - t0, timedOut: e.name === 'AbortError' };
    } finally {
        clearTimeout(timer);
    }
};

const CHECK_META = {
    checking: { label: 'Checking…', tone: 'checking', icon: 'pi pi-spin pi-spinner' },
    up: { label: 'Responding', tone: 'up', icon: 'pi pi-check-circle' },
    down: { label: 'No response', tone: 'down', icon: 'pi pi-times-circle' },
    invalid: { label: 'Invalid URL', tone: 'invalid', icon: 'pi pi-exclamation-triangle' }
};

const CHECK_TOOLTIP = {
    up: 'The host answered. Cross-origin rules hide the HTTP status, so this confirms the server is up — not that the report page renders.',
    down: 'The request failed — DNS failure, refused connection, invalid SSL certificate, timeout, or a http:// URL blocked on an https:// page.',
    invalid: 'Not a valid http:// or https:// address.',
    checking: 'Checking…'
};

const CheckChip = ({ check }) => {
    if (!check) return null;
    const meta = CHECK_META[check.state];
    return (
        <span className={`cr-check-chip is-${meta.tone}`} title={CHECK_TOOLTIP[check.state]}>
            <i className={meta.icon} />
            {meta.label}
            {check.state !== 'checking' && check.ms !== undefined && check.state !== 'invalid' && <span className="cr-check-ms">{check.timedOut ? 'timed out' : `${check.ms} ms`}</span>}
        </span>
    );
};

/** Worst-of roll-up across a client's URLs, for the single dot shown in the left list. */
const aggregateCheck = (checksForClient, urls) => {
    const done = urls.map((u) => checksForClient?.[u]).filter(Boolean);
    if (!done.length) return null;
    if (done.some((r) => r.state === 'checking')) return { state: 'checking' };
    if (done.some((r) => r.state === 'down')) return { state: 'down' };
    if (done.some((r) => r.state === 'invalid')) return { state: 'invalid' };
    return { state: 'up' };
};

const Pill = ({ tone, children }) => <span className={`cr-pill cr-pill-${tone}`}>{children}</span>;

const StatusPill = ({ status }) => <Pill tone={String(status).toLowerCase() === 'active' ? 'success' : 'secondary'}>{val(status)}</Pill>;

/** Compact user table shared by branch users and client-level users. */
const UserTable = ({ users }) => {
    if (!users.length) return <div className="cr-nodata">No users on this record.</div>;
    return (
        <div className="cr-utable-wrap">
            <table className="cr-utable">
                <thead>
                    <tr>
                        <th>User ID</th>
                        <th>Username</th>
                        <th>Password</th>
                        <th>Role</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((u, i) => (
                        <tr key={`${u.userId}-${i}`}>
                            <td className="cr-mono">{val(u.userId)}</td>
                            <td className="cr-strong">{val(u.username)}</td>
                            <td className="cr-mono">{val(u.prime_id)}</td>
                            <td>
                                <Pill tone="role">
                                    {val(u.role)}
                                    <span className="cr-role-id">#{val(u.roleId)}</span>
                                </Pill>
                            </td>
                            <td><StatusPill status={u.status} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/** Right pane body for one client: summary strip, branches (each with users), client-level users. */
const ClientDetail = ({ client, checks, onCheck, onCheckAll }) => {
    const branches = branchesOf(client);
    const clientUsers = clientUsersOf(client);
    const urls = splitReportUrls(client.report_url);
    const anyChecked = urls.some((u) => checks?.[u]);
    const busy = urls.some((u) => checks?.[u]?.state === 'checking');

    // Branches with no users carry nothing to check, so only the ones with users are listed.
    const branchesWithUsers = branches.filter((b) => usersOf(b).length > 0);
    const emptyBranchCount = branches.length - branchesWithUsers.length;

    return (
        <>
            <div className="cr-summary">
                <span className="cr-client-prefix">{val(client.branch_prefix)}</span>
                <div className="cr-summary-id">
                    <div className="cr-summary-name">{val(client.client_name)}</div>
                    <div className="cr-summary-meta">
                        <span className="cr-mono">Client ID {val(client.client_id)}</span>
                        <span>·</span>
                        <span>{branches.length} branches</span>
                        <span>·</span>
                        <span>{clientUserCount(client)} users</span>
                    </div>
                </div>
                <StatusPill status={client.status} />
            </div>

            {/* Report server check — report_url may hold more than one address. */}
            {urls.length === 0 ? (
                <div className="cr-urlrow is-missing">
                    <i className="pi pi-ban" />
                    <span className="cr-report-none">No report URL configured for this client — nothing to check.</span>
                </div>
            ) : (
                <div className="cr-urlblock">
                    <div className="cr-urlblock-head">
                        <i className="pi pi-globe" />
                        <span className="cr-urlblock-title">Report {urls.length > 1 ? `URLs (${urls.length})` : 'URL'}</span>
                        <span className="cr-spacer" />
                        <button type="button" className="cr-check-btn" onClick={onCheckAll} disabled={busy}>
                            <i className={busy ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'} /> {anyChecked ? 'Re-check' : 'Check'} {urls.length > 1 ? 'all' : 'report server'}
                        </button>
                    </div>

                    {urls.map((u, i) => (
                        <div key={u} className="cr-urlrow">
                            {urls.length > 1 && <span className="cr-url-index">{i + 1}</span>}
                            <a className="cr-url-text" href={u} target="_blank" rel="noreferrer" title={u}>
                                {u}
                            </a>
                            <CheckChip check={checks?.[u]} />
                            <button type="button" className="cr-check-one" onClick={() => onCheck(u)} disabled={checks?.[u]?.state === 'checking'} title="Check this URL">
                                <i className="pi pi-bolt" />
                            </button>
                        </div>
                    ))}

                    {anyChecked && (
                        <div className="cr-url-foot">
                            <i className="pi pi-info-circle" />
                            Reachability only — a response means the host answered; cross-origin rules hide the HTTP status, so open a link to confirm the report itself renders.
                        </div>
                    )}
                </div>
            )}

            {/* Declared vs actual branch count is the kind of mismatch this page exists to catch. */}
            {String(client.no_of_branches ?? '') !== String(branches.length) && (
                <div className="cr-warn">
                    <i className="pi pi-exclamation-triangle" />
                    <span>
                        Client declares <strong>{val(client.no_of_branches)} branches</strong> but {branches.length} Active branch{branches.length === 1 ? '' : 'es'} came back.
                    </span>
                </div>
            )}

            <div className="cr-section-label">
                <i className="pi pi-user" /> Client-level users
                <span className="cr-section-count">{clientUsers.length}</span>
            </div>
            <div className="cr-branch cr-branch-plain">
                <UserTable users={clientUsers} />
            </div>

            <div className="cr-section-label">
                <i className="pi pi-building" /> Branches
                <span className="cr-section-count">{branchesWithUsers.length}</span>
                {emptyBranchCount > 0 && (
                    <span className="cr-section-hidden">
                        {emptyBranchCount} without users hidden
                    </span>
                )}
            </div>

            {branchesWithUsers.length === 0 ? (
                <div className="cr-nodata">{branches.length === 0 ? 'No Active branches on this client.' : 'No branch on this client has users.'}</div>
            ) : (
                branchesWithUsers.map((b) => (
                    <div key={b.branch_id} className="cr-branch">
                        <div className="cr-branch-head">
                            <span className="cr-branch-slno">{val(b.branch_slno)}</span>
                            <span className="cr-branch-name">{val(b.branch_name)}</span>
                            {b.branch_title && <span className="cr-branch-title">{b.branch_title}</span>}
                            <span className="cr-mono cr-branch-id">ID {val(b.branch_id)}</span>
                            <StatusPill status={b.status} />
                            <span className="cr-spacer" />
                            <span className="cr-branch-users">{usersOf(b).length} users</span>
                        </div>
                        <UserTable users={usersOf(b)} />
                    </div>
                ))
            )}
        </>
    );
};

export default function Clientreportchecking() {
    const toast = useRef(null);

    const [clients, setClients] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listFailed, setListFailed] = useState(false);

    const [search, setSearch] = useState('');

    const [selectedId, setSelectedId] = useState(null);
    const [details, setDetails] = useState({});
    const [detailLoading, setDetailLoading] = useState(false);

    const [urlChecks, setUrlChecks] = useState({});
    const [checkingAll, setCheckingAll] = useState(false);

    // Which clients have already been fetched — kept in a ref so selecting a client
    // twice doesn't re-request, without making the click handler depend on state.
    const fetchedRef = useRef(new Set());

    // The full Active-client list is the page's default view — load it as soon as the page opens.
    useEffect(() => {
        loadList(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadList = async (autoSelect = false) => {
        setListLoading(true);
        setListFailed(false);
        try {
            const data = await getClientReportsList();
            // Filtered here so the list, the search, the hero totals and the detail pane all agree.
            const rows = clientsOf(data).filter((c) => !isHiddenClient(c));
            setClients(rows);
            if (autoSelect && rows[0]) selectClient(String(rows[0].client_id));
        } catch {
            setListFailed(true);
            toast.current?.show({ severity: 'error', summary: 'Load Failed', detail: 'Could not load the client list.' });
        } finally {
            setListLoading(false);
        }
    };

    const loadDetail = async (clientId) => {
        setDetailLoading(true);
        fetchedRef.current.add(clientId);
        try {
            const data = await getClientReportById(clientId);
            setDetails((prev) => ({ ...prev, [clientId]: { ok: true, client: clientsOf(data)[0] || null } }));
        } catch {
            // Drop it from the fetched set so re-selecting retries instead of showing a stale error.
            fetchedRef.current.delete(clientId);
            setDetails((prev) => ({ ...prev, [clientId]: { ok: false, client: null } }));
            toast.current?.show({ severity: 'error', summary: 'Load Failed', detail: 'Could not load this client.' });
        } finally {
            setDetailLoading(false);
        }
    };

    // Already-loaded clients come straight from cache; Refresh in the pane re-fetches.
    const selectClient = (clientId) => {
        setSelectedId(clientId);
        if (!fetchedRef.current.has(clientId)) loadDetail(clientId);
    };

    // Results are nested per client, then per URL, since one client can list several.
    const setCheck = (clientId, url, result) => setUrlChecks((prev) => ({ ...prev, [clientId]: { ...(prev[clientId] || {}), [url]: result } }));

    const checkReportUrl = async (clientId, url) => {
        if (!isHttpUrl(url)) {
            setCheck(clientId, url, { state: 'invalid' });
            return;
        }
        setCheck(clientId, url, { state: 'checking' });
        setCheck(clientId, url, await probeUrl(url));
    };

    // Sequential on purpose — a handful of report hosts, checked one at a time so a
    // slow host doesn't compete with the others for the browser's connection budget.
    const checkClientUrls = async (clientId, urls) => {
        for (const u of urls) {
            // eslint-disable-next-line no-await-in-loop
            await checkReportUrl(clientId, u);
        }
    };

    const checkAllReportUrls = async () => {
        const targets = clients.map((c) => ({ id: String(c.client_id), urls: splitReportUrls(c.report_url) })).filter((t) => t.urls.length);
        if (!targets.length) {
            toast.current?.show({ severity: 'info', summary: 'Nothing to check', detail: 'No client has a report URL configured.' });
            return;
        }
        setCheckingAll(true);
        for (const t of targets) {
            // eslint-disable-next-line no-await-in-loop
            await checkClientUrls(t.id, t.urls);
        }
        setCheckingAll(false);
    };

    // Matches on client name, ID or branch prefix — the three things visible on a row.
    const visibleClients = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return clients;
        return clients.filter((c) => [c.client_name, c.client_id, c.branch_prefix].some((v) => String(v || '').toLowerCase().includes(q)));
    }, [clients, search]);

    const totals = treeTotals({ clients });
    const detail = selectedId ? details[selectedId] : null;
    const listedClient = clients.find((c) => String(c.client_id) === String(selectedId)) || null;

    const renderDetailPane = () => {
        if (!selectedId) {
            return (
                <div className="cr-empty">
                    <div className="cr-empty-icon">
                        <i className="pi pi-sitemap" />
                    </div>
                    <h4>No client selected</h4>
                    <p>Pick a client on the left to see its branches, branch users and client-level users.</p>
                </div>
            );
        }

        return (
            <>
                <div className="cr-detail-header">
                    <div className="cr-crumb">
                        <i className="pi pi-sitemap" style={{ fontSize: '0.75rem' }} />
                        <span>Client Report</span>
                    </div>
                    <div className="cr-detail-title">
                        <h3>{val(detail?.client?.client_name || listedClient?.client_name)}</h3>
                        <span className="cr-spacer" />
                        <Button label="Refresh" icon="pi pi-refresh" className="cr-refresh" onClick={() => loadDetail(selectedId)} loading={detailLoading} />
                    </div>
                </div>

                <div className="cr-detail-body">
                    {detailLoading && !detail ? (
                        <div className="cr-hint cr-hint-loading">
                            <i className="pi pi-spin pi-spinner" />
                            <span>Loading client…</span>
                        </div>
                    ) : detail && !detail.ok ? (
                        <div className="cr-hint cr-hint-error">
                            <i className="pi pi-exclamation-circle" />
                            <span>Could not load this client. Use Refresh to try again.</span>
                        </div>
                    ) : detail?.client ? (
                        <ClientDetail
                            client={detail.client}
                            checks={urlChecks[selectedId]}
                            onCheck={(url) => checkReportUrl(selectedId, url)}
                            onCheckAll={() => checkClientUrls(selectedId, splitReportUrls(detail.client.report_url))}
                        />
                    ) : (
                        detail && <div className="cr-nodata">This client returned no Active record.</div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div className="clientreport-page">
            <Toast ref={toast} />
            <style>{`
                .clientreport-page { padding: 1.25rem; background: #f4f5fa; min-height: 100%; }

                /* ── Hero ───────────────────────────────────────────── */
                .clientreport-page .cr-hero { position: relative; overflow: hidden; border-radius: 18px; padding: 22px 26px; margin-bottom: 16px;
                    background: linear-gradient(135deg, #4c1d95 0%, #5b2fa0 45%, #7c3aed 100%);
                    box-shadow: 0 12px 30px -12px rgba(76, 29, 149, 0.55); display: flex; gap: 24px; align-items: center; flex-wrap: wrap; }
                .clientreport-page .cr-hero::after { content: ''; position: absolute; right: -70px; top: -110px; width: 320px; height: 320px; border-radius: 50%;
                    background: radial-gradient(circle, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 68%); pointer-events: none; }
                .clientreport-page .cr-hero-main { display: flex; align-items: center; gap: 16px; flex: 1; min-width: 280px; position: relative; z-index: 1; }
                .clientreport-page .cr-hero-icon { width: 52px; height: 52px; flex-shrink: 0; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.25); backdrop-filter: blur(6px); }
                .clientreport-page .cr-hero-icon i { font-size: 1.5rem; color: #fff; }
                .clientreport-page .cr-hero-title { margin: 0; font-size: 1.4rem; font-weight: 800; color: #fff; letter-spacing: -0.02em; line-height: 1.2; }
                .clientreport-page .cr-hero-sub { margin: 5px 0 0; font-size: 0.82rem; color: rgba(255,255,255,0.78); max-width: 660px; line-height: 1.45; }

                .clientreport-page .cr-stats { display: flex; gap: 10px; flex-wrap: wrap; position: relative; z-index: 1; }
                .clientreport-page .cr-stat { min-width: 104px; padding: 10px 14px; border-radius: 12px; background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.18); backdrop-filter: blur(8px); transition: transform .18s ease, background-color .18s ease; }
                .clientreport-page .cr-stat:hover { transform: translateY(-2px); background: rgba(255,255,255,0.18); }
                .clientreport-page .cr-stat-value { font-size: 1.4rem; font-weight: 800; color: #fff; line-height: 1.1; display: flex; align-items: center; gap: 7px; }
                .clientreport-page .cr-stat-value i { font-size: 0.8rem; }
                .clientreport-page .cr-stat-label { font-size: 0.63rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: rgba(255,255,255,0.7); margin-top: 3px; }
                .clientreport-page .cr-stat.c1 .cr-stat-value i { color: #c4b5fd; }
                .clientreport-page .cr-stat.c2 .cr-stat-value i { color: #93c5fd; }
                .clientreport-page .cr-stat.c3 .cr-stat-value i { color: #6ee7b7; }

                /* ── Split view ─────────────────────────────────────── */
                .clientreport-page .cr-split { display: flex; gap: 16px; height: 74vh; min-height: 540px; }
                .clientreport-page .cr-sidebar, .clientreport-page .cr-detail { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); overflow-y: auto; }
                .clientreport-page .cr-sidebar { width: 330px; flex-shrink: 0; }
                .clientreport-page .cr-detail { flex: 1; display: flex; flex-direction: column; }
                .clientreport-page .cr-sidebar::-webkit-scrollbar, .clientreport-page .cr-detail::-webkit-scrollbar { width: 8px; height: 8px; }
                .clientreport-page .cr-sidebar::-webkit-scrollbar-thumb, .clientreport-page .cr-detail::-webkit-scrollbar-thumb { background: #dfe3ec; border-radius: 8px; }
                .clientreport-page .cr-sidebar::-webkit-scrollbar-thumb:hover, .clientreport-page .cr-detail::-webkit-scrollbar-thumb:hover { background: #c7cddb; }

                /* Sidebar header */
                .clientreport-page .cr-sidebar-head { position: sticky; top: 0; z-index: 3; display: flex; align-items: center; gap: 8px;
                    padding: 12px 14px; background: #fff; border-bottom: 1px solid #eef0f6; }
                .clientreport-page .cr-sidebar-title { font-size: 0.68rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.09em; color: #5b21b6; }
                .clientreport-page .cr-sidebar-head i.pi-briefcase { color: #7c3aed; font-size: 0.8rem; }
                .clientreport-page .cr-group-count { font-weight: 700; font-size: 0.68rem; padding: 1px 8px; border-radius: 999px; background: #f5f3ff; border: 1px solid #e0d9fb; color: #5b21b6; }
                .clientreport-page .cr-reload { margin-left: auto; display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 8px; cursor: pointer;
                    background: #f4f5f9; border: 1px solid #e2e5ef; color: #64748b; font-size: 0.68rem; font-weight: 700; }
                .clientreport-page .cr-reload:hover { background: #e9ebf2; color: #475569; }

                /* Sidebar search */
                .clientreport-page .cr-search { position: sticky; top: 53px; z-index: 3; display: flex; align-items: center; padding: 10px 12px;
                    background: #fff; border-bottom: 1px solid #eef0f6; }
                .clientreport-page .cr-search .p-input-icon-left { width: 100%; }
                .clientreport-page .cr-search .p-inputtext { width: 100%; border-radius: 10px; background: #f7f8fc; border-color: #e8eaf2;
                    font-size: 0.83rem; padding-left: 2.4rem; padding-right: 2rem; height: 2.35rem; }
                .clientreport-page .cr-search .p-inputtext:enabled:focus { background: #fff; border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12); }
                .clientreport-page .cr-search .pi-search { color: #a5abbd; }
                .clientreport-page .cr-search-clear { position: absolute; right: 22px; cursor: pointer; color: #a5abbd; font-size: 0.72rem; padding: 4px; }
                .clientreport-page .cr-search-clear:hover { color: #64748b; }

                /* Client list items */
                .clientreport-page .cr-item { cursor: pointer; padding: 11px 14px 11px 15px; border-left: 3px solid transparent; border-bottom: 1px solid #f1f3f8;
                    display: flex; align-items: center; gap: 10px; transition: background-color .15s ease, border-color .15s ease, padding-left .15s ease; }
                .clientreport-page .cr-item:hover { background: #f8f9fd; padding-left: 18px; }
                .clientreport-page .cr-item.selected { background: linear-gradient(90deg, #f5f3ff 0%, #fbfaff 100%); border-left-color: #7c3aed; }
                .clientreport-page .cr-item.selected .cr-item-title { color: #5b21b6; }
                .clientreport-page .cr-item-badge { width: 34px; height: 34px; flex-shrink: 0; border-radius: 10px; display: flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, #6d28d9, #8b5cf6); color: #fff; font-weight: 800; font-size: 0.7rem; letter-spacing: 0.03em; }
                .clientreport-page .cr-item-main { flex: 1; min-width: 0; }
                .clientreport-page .cr-item-title { font-weight: 700; font-size: 0.86rem; color: #1e293b; line-height: 1.3;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .clientreport-page .cr-item-meta { display: flex; align-items: center; gap: 6px; font-size: 0.7rem; color: #a5abbd; margin-top: 3px; }
                .clientreport-page .cr-list-state { padding: 26px 16px; text-align: center; color: #a5abbd; font-size: 0.83rem; }
                .clientreport-page .cr-list-state i { display: block; font-size: 1.6rem; margin-bottom: 8px; color: #cbd5e1; }

                /* Pills */
                .clientreport-page .cr-pill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px;
                    font-size: 0.63rem; font-weight: 700; letter-spacing: 0.03em; line-height: 1.6; border: 1px solid transparent; white-space: nowrap; }
                .clientreport-page .cr-pill-success { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .clientreport-page .cr-pill-secondary { background: #f4f5f9; color: #64748b; border-color: #e2e5ef; }
                .clientreport-page .cr-pill-role { background: #f5f3ff; color: #5b21b6; border-color: #ddd6fe; }
                .clientreport-page .cr-role-id { opacity: 0.55; font-weight: 600; }

                /* Detail header */
                .clientreport-page .cr-detail-header { padding: 18px 24px; border-bottom: 1px solid #eef0f6; position: sticky; top: 0; z-index: 2; backdrop-filter: blur(6px);
                    background: linear-gradient(180deg, rgba(124, 58, 237, 0.10) 0%, rgba(255,255,255,0.92) 100%); }
                .clientreport-page .cr-crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
                    letter-spacing: 0.08em; margin-bottom: 8px; color: #6d28d9; }
                .clientreport-page .cr-detail-title { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
                .clientreport-page .cr-detail-title h3 { margin: 0; font-size: 1.28rem; font-weight: 800; color: #1e293b; letter-spacing: -0.02em; }
                .clientreport-page .cr-refresh.p-button { border-radius: 10px; font-weight: 600; font-size: 0.8rem; padding: 0.45rem 0.9rem; border: none;
                    background: linear-gradient(135deg, #6d28d9, #7c3aed); box-shadow: 0 6px 14px -6px rgba(109, 40, 217, 0.8); }
                .clientreport-page .cr-refresh.p-button:enabled:hover { background: linear-gradient(135deg, #5b21b6, #6d28d9); }
                .clientreport-page .cr-detail-body { padding: 18px 24px 24px; }

                /* Hints */
                .clientreport-page .cr-hint { display: flex; align-items: flex-start; gap: 9px; padding: 12px 14px; border-radius: 10px;
                    background: #f8f9fd; border: 1px dashed #e2e5ef; color: #7c8598; font-size: 0.8rem; line-height: 1.5; }
                .clientreport-page .cr-hint i { margin-top: 2px; color: #a78bfa; }
                .clientreport-page .cr-hint-loading { background: #f5f3ff; border-color: #ddd6fe; color: #6d28d9; }
                .clientreport-page .cr-hint-error { background: #fef2f2; border-color: #fecaca; color: #b91c1c; }
                .clientreport-page .cr-hint-error i { color: #ef4444; }

                /* Client summary */
                .clientreport-page .cr-summary { display: flex; align-items: center; gap: 12px; padding: 12px 14px; margin-bottom: 12px; flex-wrap: wrap;
                    background: linear-gradient(90deg, #faf9ff, #fff); border: 1px solid #e8e4fb; border-radius: 14px; }
                .clientreport-page .cr-client-prefix { width: 40px; height: 40px; flex-shrink: 0; border-radius: 11px; display: flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, #6d28d9, #8b5cf6); color: #fff; font-weight: 800; font-size: 0.78rem; letter-spacing: 0.04em; }
                .clientreport-page .cr-summary-id { flex: 1; min-width: 0; }
                .clientreport-page .cr-summary-name { font-size: 0.98rem; font-weight: 800; color: #1e293b; }
                .clientreport-page .cr-summary-meta { display: flex; align-items: center; gap: 6px; font-size: 0.72rem; color: #94a3b8; margin-top: 2px; flex-wrap: wrap; }
                .clientreport-page .cr-report-none { font-size: 0.76rem; color: #a5abbd; font-weight: 600; }

                /* Report-server check */
                .clientreport-page .cr-urlblock { margin-bottom: 10px; background: #fbfbfe; border: 1px solid #e8eaf2; border-radius: 12px; overflow: hidden; }
                .clientreport-page .cr-urlblock-head { display: flex; align-items: center; gap: 9px; padding: 9px 13px; background: #f8f9fd; border-bottom: 1px solid #eef0f6; }
                .clientreport-page .cr-urlblock-head > .pi-globe { color: #7c3aed; font-size: 0.85rem; }
                .clientreport-page .cr-urlblock-title { font-size: 0.68rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.09em; color: #5b21b6; }
                .clientreport-page .cr-urlrow { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 9px 13px; border-bottom: 1px solid #f4f5f9; }
                .clientreport-page .cr-urlblock .cr-urlrow:last-of-type { border-bottom: none; }
                .clientreport-page .cr-urlrow > .pi-ban { color: #cbd5e1; font-size: 0.85rem; }
                .clientreport-page .cr-urlrow.is-missing { margin-bottom: 10px; border: 1px dashed #e8eaf2; border-radius: 12px; background: #fcfcfe; }
                .clientreport-page .cr-url-index { width: 20px; height: 20px; flex-shrink: 0; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center;
                    background: #ede9fe; color: #5b21b6; font-size: 0.66rem; font-weight: 800; }
                .clientreport-page .cr-check-one { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 8px;
                    cursor: pointer; background: #f5f3ff; border: 1px solid #e0d9fb; color: #6d28d9; font-size: 0.7rem; }
                .clientreport-page .cr-check-one:hover:not(:disabled) { background: #ede9fe; }
                .clientreport-page .cr-check-one:disabled { background: #f4f5f9; border-color: #e2e5ef; color: #cbd5e1; cursor: default; }
                .clientreport-page .cr-url-foot { display: flex; align-items: flex-start; gap: 8px; padding: 9px 13px; background: #f8f9fd; border-top: 1px solid #eef0f6;
                    font-size: 0.72rem; color: #94a3b8; line-height: 1.45; }
                .clientreport-page .cr-url-foot i { margin-top: 2px; }
                .clientreport-page .cr-url-text { flex: 1; min-width: 180px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.76rem;
                    color: #1d4ed8; text-decoration: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .clientreport-page .cr-url-text:hover { text-decoration: underline; }
                .clientreport-page .cr-check-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 9px; cursor: pointer;
                    border: none; color: #fff; font-size: 0.74rem; font-weight: 700; white-space: nowrap;
                    background: linear-gradient(135deg, #6d28d9, #7c3aed); box-shadow: 0 5px 12px -6px rgba(109, 40, 217, 0.9); }
                .clientreport-page .cr-check-btn:hover:not(:disabled) { background: linear-gradient(135deg, #5b21b6, #6d28d9); }
                .clientreport-page .cr-check-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: default; }

                .clientreport-page .cr-check-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px;
                    font-size: 0.72rem; font-weight: 700; border: 1px solid transparent; white-space: nowrap; }
                .clientreport-page .cr-check-chip i { font-size: 0.75rem; }
                .clientreport-page .cr-check-chip.is-up { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .clientreport-page .cr-check-chip.is-down { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
                .clientreport-page .cr-check-chip.is-invalid { background: #fffbeb; color: #92400e; border-color: #fde68a; }
                .clientreport-page .cr-check-chip.is-checking { background: #f5f3ff; color: #6d28d9; border-color: #ddd6fe; }
                .clientreport-page .cr-check-ms { font-weight: 600; opacity: 0.7; }

                .clientreport-page .cr-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-left: 7px; vertical-align: middle; flex-shrink: 0; }
                .clientreport-page .cr-dot.is-up { background: #10b981; box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.18); }
                .clientreport-page .cr-dot.is-down { background: #ef4444; box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.18); }
                .clientreport-page .cr-dot.is-invalid { background: #f59e0b; box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.18); }
                .clientreport-page .cr-dot.is-checking { background: #a78bfa; animation: crPulse 1s ease-in-out infinite; }
                .clientreport-page .cr-dot.is-none { background: transparent; border: 1px dashed #cbd5e1; }
                @keyframes crPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

                .clientreport-page .cr-checkall { margin-left: auto; }
                .clientreport-page .cr-checkall + .cr-reload { margin-left: 0; }
                .clientreport-page .cr-spacer { flex: 1; }

                .clientreport-page .cr-warn { display: flex; align-items: center; gap: 9px; padding: 9px 12px; margin-bottom: 12px; border-radius: 10px;
                    background: #fffbeb; border: 1px solid #fde68a; color: #92400e; font-size: 0.78rem; }
                .clientreport-page .cr-section-label { display: flex; align-items: center; gap: 7px; font-size: 0.67rem; font-weight: 800; text-transform: uppercase;
                    letter-spacing: 0.09em; color: #7c3aed; margin: 16px 0 8px; }
                .clientreport-page .cr-section-count { padding: 1px 8px; border-radius: 999px; background: #f5f3ff; border: 1px solid #e0d9fb; color: #5b21b6; }
                .clientreport-page .cr-section-hidden { padding: 1px 8px; border-radius: 999px; background: #f4f5f9; border: 1px solid #e2e5ef;
                    color: #94a3b8; font-weight: 700; text-transform: none; letter-spacing: 0.02em; }

                .clientreport-page .cr-branch { border: 1px solid #eef0f6; border-radius: 12px; overflow: hidden; margin-bottom: 10px; background: #fff; }
                .clientreport-page .cr-branch-plain { border-style: dashed; }
                .clientreport-page .cr-branch-head { display: flex; align-items: center; gap: 9px; padding: 9px 12px; background: #f8f9fd; border-bottom: 1px solid #eef0f6; flex-wrap: wrap; }
                .clientreport-page .cr-branch-slno { width: 22px; height: 22px; flex-shrink: 0; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center;
                    background: #ede9fe; color: #5b21b6; font-size: 0.68rem; font-weight: 800; }
                .clientreport-page .cr-branch-name { font-size: 0.86rem; font-weight: 700; color: #1e293b; }
                .clientreport-page .cr-branch-title { font-size: 0.7rem; font-weight: 700; color: #0f766e; background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 999px; padding: 1px 8px; }
                .clientreport-page .cr-branch-id { font-size: 0.7rem; color: #94a3b8; }
                .clientreport-page .cr-branch-users { font-size: 0.7rem; color: #94a3b8; font-weight: 600; }

                .clientreport-page .cr-utable-wrap { overflow-x: auto; }
                .clientreport-page .cr-utable { width: 100%; border-collapse: collapse; font-size: 0.78rem; }
                .clientreport-page .cr-utable th { text-align: left; padding: 7px 10px; background: #f5f3ff; color: #5b21b6; font-size: 0.64rem; font-weight: 800;
                    text-transform: uppercase; letter-spacing: 0.07em; border-bottom: 1px solid #eae7f7; white-space: nowrap; }
                .clientreport-page .cr-utable td { padding: 7px 10px; border-bottom: 1px solid #f4f5f9; color: #475569; white-space: nowrap; }
                .clientreport-page .cr-utable tr:last-child td { border-bottom: none; }
                .clientreport-page .cr-utable tbody tr:hover { background: #fbfaff; }
                .clientreport-page .cr-mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.74rem; color: #64748b; }
                .clientreport-page .cr-strong { font-weight: 700; color: #1e293b; }
                .clientreport-page .cr-nodata { padding: 14px; text-align: center; color: #a5abbd; font-size: 0.78rem; }

                /* Empty detail state */
                .clientreport-page .cr-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; text-align: center; padding: 40px; }
                .clientreport-page .cr-empty-icon { width: 88px; height: 88px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, #f5f3ff, #ede9fe); margin-bottom: 18px; }
                .clientreport-page .cr-empty-icon i { font-size: 2.2rem; color: #a78bfa; }
                .clientreport-page .cr-empty h4 { margin: 0 0 6px; font-size: 1.02rem; font-weight: 700; color: #475569; }
                .clientreport-page .cr-empty p { margin: 0; font-size: 0.84rem; color: #94a3b8; max-width: 380px; line-height: 1.55; }

                @media (max-width: 1100px) {
                    .clientreport-page .cr-split { flex-direction: column; height: auto; }
                    .clientreport-page .cr-sidebar { width: 100%; max-height: 320px; }
                    .clientreport-page .cr-detail { min-height: 440px; }
                }
                @media (max-width: 640px) {
                    .clientreport-page { padding: 0.75rem; }
                    .clientreport-page .cr-hero { padding: 18px; }
                    .clientreport-page .cr-stat { flex: 1; min-width: 88px; }
                    .clientreport-page .cr-detail-header, .clientreport-page .cr-detail-body { padding-left: 16px; padding-right: 16px; }
                }
            `}</style>

            {/* ── Hero ───────────────────────────────────────────── */}
            <div className="cr-hero">
                <div className="cr-hero-main">
                    <div className="cr-hero-icon">
                        <i className="pi pi-sitemap" />
                    </div>
                    <div>
                        <h2 className="cr-hero-title">Client Reports</h2>
                        <p className="cr-hero-sub">All Active clients load on open. Pick a client on the left to see its branches, branch users and client-level users on the right.</p>
                    </div>
                </div>

                <div className="cr-stats">
                    <div className="cr-stat c1">
                        <div className="cr-stat-value">
                            <i className="pi pi-briefcase" />
                            {totals.clients}
                        </div>
                        <div className="cr-stat-label">Clients</div>
                    </div>
                    <div className="cr-stat c2">
                        <div className="cr-stat-value">
                            <i className="pi pi-building" />
                            {totals.branches}
                        </div>
                        <div className="cr-stat-label">Branches</div>
                    </div>
                    <div className="cr-stat c3">
                        <div className="cr-stat-value">
                            <i className="pi pi-users" />
                            {totals.users}
                        </div>
                        <div className="cr-stat-label">Users</div>
                    </div>
                </div>
            </div>

            {/* ── Split view ─────────────────────────────────────── */}
            <div className="cr-split">
                {/* Left: Active clients */}
                <div className="cr-sidebar">
                    <div className="cr-sidebar-head">
                        <i className="pi pi-briefcase" />
                        <span className="cr-sidebar-title">Active Clients</span>
                        <span className="cr-group-count">{search.trim() ? `${visibleClients.length} / ${clients.length}` : clients.length}</span>
                        <button type="button" className="cr-reload cr-checkall" onClick={checkAllReportUrls} disabled={checkingAll || !clients.length} title="Check every client's report server">
                            <i className={checkingAll ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'} /> Check URLs
                        </button>
                        <button type="button" className="cr-reload" onClick={() => loadList(false)} disabled={listLoading} title="Reload the client list">
                            <i className={listLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} />
                        </button>
                    </div>

                    <div className="cr-search">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, ID or prefix…" />
                        </span>
                        {search && <i className="pi pi-times cr-search-clear" onClick={() => setSearch('')} title="Clear search" />}
                    </div>

                    {listLoading && !clients.length ? (
                        <div className="cr-list-state">
                            <i className="pi pi-spin pi-spinner" />
                            Loading clients…
                        </div>
                    ) : listFailed && !clients.length ? (
                        <div className="cr-list-state">
                            <i className="pi pi-exclamation-circle" />
                            Could not load the client list.
                        </div>
                    ) : clients.length === 0 ? (
                        <div className="cr-list-state">
                            <i className="pi pi-inbox" />
                            No Active clients found.
                        </div>
                    ) : visibleClients.length === 0 ? (
                        <div className="cr-list-state">
                            <i className="pi pi-search" />
                            No client matches “{search.trim()}”.
                        </div>
                    ) : (
                        visibleClients.map((c) => {
                            const id = String(c.client_id);
                            const isSelected = id === String(selectedId);
                            const clientUrls = splitReportUrls(c.report_url);
                            const check = aggregateCheck(urlChecks[id], clientUrls);
                            return (
                                <div key={id} className={`cr-item${isSelected ? ' selected' : ''}`} onClick={() => selectClient(id)}>
                                    <span className="cr-item-badge">{val(c.branch_prefix)}</span>
                                    <div className="cr-item-main">
                                        <div className="cr-item-title">{val(c.client_name)}</div>
                                        <div className="cr-item-meta">
                                            <span className="cr-mono">ID {val(c.client_id)}</span>
                                            <span>·</span>
                                            <span>{branchesOf(c).length} branches</span>
                                            <span>·</span>
                                            <span>{clientUserCount(c)} users</span>
                                        </div>
                                    </div>
                                    {check ? (
                                        <span
                                            className={`cr-dot is-${CHECK_META[check.state].tone}`}
                                            title={`Report server: ${CHECK_META[check.state].label}${clientUrls.length > 1 ? ` (worst of ${clientUrls.length} URLs)` : ''}`}
                                        />
                                    ) : (
                                        !clientUrls.length && <span className="cr-dot is-none" title="No report URL configured" />
                                    )}
                                    {isSelected && detailLoading ? <i className="pi pi-spin pi-spinner" style={{ color: '#7c3aed', fontSize: '0.8rem' }} /> : <StatusPill status={c.status} />}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Right: selected client details */}
                <div className="cr-detail">{renderDetailPane()}</div>
            </div>
        </div>
    );
}
